import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Cell, Label, Pie, PieChart, Tooltip } from 'recharts'
import { getFontSize } from '../../../helpers/fontSize'
import { UseTypeFontSize } from '../../hooks/useTypeFontSize'
import { deleteChartData, selectChartData, setChartData } from '../store'
import { CHART_TOOLTIP } from './constants'

export const PieChartTwoLevelContainer = ({
  chartId,
  dataInner,
  dataOuter,
  width,
  height,
  innerPieProps = {},
  outerPieProps = {},
  isTooltipActive = true,
  formatValueTooltip,
  formatNameTooltip,
  renderCustomTooltip,
  propsTooltip = {},
  onPieClick,
  isDisableActiveIndex = false,
  defaultActiveIndex,
  labelCenter,
  ...rest
}) => {
  const dispatch = useDispatch()
  const typeFontSize = UseTypeFontSize()
  const fontSize = getFontSize(12, typeFontSize)
  const activeIndex = useSelector(
    selectChartData(chartId, (data) => data.activeIndex),
  )

  useEffect(() => {
    if (chartId) {
      dispatch(setChartData({ chartId }))
      return () => {
        dispatch(deleteChartData({ chartId }))
      }
    }
  }, [chartId])

  // Tooltip
  const renderTooltip = () => {
    if (!isTooltipActive) return null
    return (
      <Tooltip
        wrapperStyle={CHART_TOOLTIP.wrapperStyle}
        labelStyle={{ ...CHART_TOOLTIP.labelStyle, fontSize }}
        cursor={CHART_TOOLTIP.cursor}
        allowEscapeViewBox={{ x: true, y: true }}
        isAnimationActive={false}
        content={renderCustomTooltip}
        {...propsTooltip}
      />
    )
  }

  const renderPieLabel = (props) => {
    const { percent, cx, cy, midAngle, innerRadius, outerRadius } = props
    const RADIAN = Math.PI / 180
    const radius = innerRadius + (outerRadius - innerRadius) * 0.1
    const x = cx + radius * Math.cos(-midAngle * RADIAN)
    const y = cy + radius * Math.sin(-midAngle * RADIAN)

    return (
      <text
        x={x}
        y={y}
        fill="#fff"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={10}
        fontWeight={600}
      >
        {`${(percent * 100).toFixed(2)}%`}
      </text>
    )
  }

  // Actions
  const onPieOver = (_, index) => {
    if (chartId) {
      dispatch(setChartData({ chartId, activeIndex: index }))
    }
  }

  const onPieLeave = () => {
    if (chartId) {
      dispatch(setChartData({ chartId, activeIndex: undefined }))
    }
  }

  return (
    <PieChart width={width} height={height} {...rest}>
      {/* Outer Pie */}
      <Pie
        activeIndex={
          (activeIndex || activeIndex === 0) && !isDisableActiveIndex
            ? activeIndex
            : defaultActiveIndex
        }
        data={dataOuter}
        dataKey="value"
        cx="50%"
        cy="50%"
        startAngle={90}
        endAngle={-270}
        outerRadius={innerPieProps.outerRadius || 60}
        isAnimationActive={false}
        {...innerPieProps}
        label={renderPieLabel}
        labelLine={false}
        onMouseOver={onPieOver}
        onMouseLeave={onPieLeave}
      >
        {dataOuter.map((item, index) => (
          <Cell key={`inner-cell-${index}`} fill={item.color} />
        ))}
        <Label
          value={labelCenter?.title || ''}
          position="center"
          style={{
            ...labelCenter?.style,
          }}
        />
      </Pie>
      {/* Inner Pie with Label */}
      <Pie
        activeIndex={
          (activeIndex || activeIndex === 0) && !isDisableActiveIndex
            ? activeIndex
            : defaultActiveIndex
        }
        data={dataInner}
        dataKey="value"
        cx="50%"
        cy="50%"
        outerRadius={outerPieProps.outerRadius || 90}
        innerRadius={outerPieProps.innerRadius || 70}
        onClick={onPieClick}
        startAngle={90}
        endAngle={-270}
        isAnimationActive={false}
        {...outerPieProps}
        label={renderPieLabel}
        labelLine={false}
        onMouseOver={onPieOver}
        onMouseLeave={onPieLeave}
      >
        {dataInner.map((item, index) => (
          <Cell key={`outer-cell-${index}`} fill={item.color} />
        ))}
      </Pie>
      {renderTooltip()}
    </PieChart>
  )
}

PieChartTwoLevelContainer.propTypes = {
  chartId: PropTypes.string,
  dataInner: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
    }),
  ).isRequired,
  dataOuter: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
    }),
  ).isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  innerPieProps: PropTypes.object,
  outerPieProps: PropTypes.object,
  isTooltipActive: PropTypes.bool,
  formatValueTooltip: PropTypes.func,
  formatNameTooltip: PropTypes.func,
  renderCustomTooltip: PropTypes.func,
  propsTooltip: PropTypes.object,
  onPieClick: PropTypes.func,
  isDisableActiveIndex: PropTypes.bool,
  defaultActiveIndex: PropTypes.number,
  labelCenter: PropTypes.shape({
    title: PropTypes.string,
    style: PropTypes.object,
    customLabel: PropTypes.func,
  }),
}

PieChartTwoLevelContainer.defaultProps = {
  dataInner: [],
  dataOuter: [],
  innerPieProps: {},
  outerPieProps: {},
  isTooltipActive: true,
  propsTooltip: {},
  isDisableActiveIndex: false,
  labelCenter: {
    title: '',
    style: {},
  },
}
